import { useState } from 'react'
import { ErrorMessage, Form, Formik } from 'formik';
import { Button, Header, Grid, Message, Icon, Label } from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';
import { UserFormValues } from '../../app/models/user';
import { returnPhoneOrEmpty } from '../../app/common/util/util';
import { stateOptions } from '../../app/common/options/stateOptions';
import { observer } from 'mobx-react-lite';
import ValidationErrors from '../errors/ValidationErrors';
import { registerCommonValidationSchema, registerValidationSchema } from '../../app/common/form/FormikFormValidations';
import MyTextInput from '../../app/common/form/MyTextInput';
import MyPhoneInput from '../../app/common/form/MyPhoneInput';
import MySelectInput from '../../app/common/form/MySelectInput';
import * as Yup from 'yup';
import MyZipCodeInput from '../../app/common/form/MyZipCodeInput';

interface Props {
    isRedirect: boolean
}

export default observer(function RegisterForm({ 
    isRedirect
 }: Props) {
    const {userStore} = useStore();
    const { register, user } = userStore;
    const [changePassword, setChangePassword] = useState(false);

    return (
        <Formik
            initialValues={new UserFormValues(user!)}
            onSubmit={(values, {setErrors}) => register(values, isRedirect).catch(error => 
                setErrors({error}))}
            validationSchema={() => Yup.lazy((values) => {
                // new user
                if (+values.subscriberID === 0) {
                    return registerValidationSchema;
                } else { // logged-in user
                    // update user data include password
                    if (+values.subscriberID > 0 && changePassword) {
                        return registerValidationSchema;
                    } else {
                        // update user data except password
                        return registerCommonValidationSchema;
                    }
                }
            })}
        >
        {({handleSubmit, isSubmitting, errors, dirty, values, isValid}) => (
            <Form className='ui form error' onSubmit={handleSubmit} autoComplete='off'>
                <Grid>
                    <Grid.Row>
                        <Grid.Column width={16}>
                            <Header
                                as='h2'
                                content={values.subscriberID > 0 ? 'Update my WSF Carpool/Vanpool Account' : 'Create my WSF Carpool/Vanpool Account'}
                                color='teal'
                            />
                            <Message icon>
                                <Icon name={values.subscriberID > 0 ? 'search' : 'folder'} />
                                <Message.Content>
                                    <Message.Header>{'WSF Carpool/Vanpool Account'}</Message.Header>
                                    {values.subscriberID > 0 ? `Please review/update your WSF Carpool/Vanpool Account Information. Once you proceed your account will be updated to reflect any changes you've made.` : `You are creating a WSF Carpool/Vanpool Account which will allow you order and renew your permits. After your account is created you may use your email address and password to login and update your account information`}
                                
                                    {values.subscriberID > 0  &&
                                        <div>
                                            <Label basic
                                                onClick={() => setChangePassword(!changePassword)}
                                                size='large'
                                                className='cursor-pointer'
                                                color='blue'
                                            >{changePassword ? 'No need to change my password' : 'Change my password'}</Label>
                                        </div>
                                    }
                                </Message.Content>
                            </Message>
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column width={16}>
                            <Header
                                content='Username and Password'
                                color='teal'
                                sub
                            />
                        </Grid.Column>
                        <Grid.Column computer={8} tablet={8} mobile={16}>

                            {values.subscriberID === 0 ? (
                                <>                    
                                    <label>Email</label>
                                    <MyTextInput name='subscriberEmail' placeholder='Email' tabIndex={1} />
                                </>
                            ) : (
                                <div className='field'>
                                    <div className='user-settings-label'>
                                        Email
                                    </div>
                                    <div className='user-settings-value'>
                                        {values.subscriberEmail}
                                    </div>
                                </div>
                            )}
                            
                            {(changePassword || values.subscriberID === 0) &&
                                <>
                                    <label>
                                        {values.subscriberID > 0 ?
                                        'New Password' : 'Password'}
                                    </label>
                                    <MyTextInput name='securityOverrideKeyword' type='password'
                                        placeholder={values.subscriberID > 0 ?
                                            'New Password' : 'Password'} tabIndex={3} />
                                </>
                            }
                        </Grid.Column>
                        <Grid.Column computer={8} tablet={8} mobile={16}>
                            {errors && values.subscriberID > 0 && returnPhoneOrEmpty(values.subscriberPhone) === '' && (
                                <Message icon color='red'>
                                    <Icon name='exclamation' />
                                    <Message.Content>
                                        <Message.Header>Invalid Phone</Message.Header>
                                        Please re-enter phone number as the following format (123) 123-1234.
                                    </Message.Content>
                                </Message>
                            )}

                            <label>Phone</label>
                            <MyPhoneInput name='subscriberPhone' placeholder='Phone' tabIndex={2} />

                            {(changePassword || values.subscriberID === 0) &&
                                <>
                                    <label>
                                        {values.subscriberID > 0 ?
                                        'Confirm New Password' : 'Confirm Password'}
                                    </label>
                                    <MyTextInput name='securityOverrideKeyword2' type='password'
                                        placeholder={values.subscriberID > 0 ?
                                            'Confirm New Password' : 'Confirm Password'} tabIndex={4} />
                                </>
                            }
                        </Grid.Column>
                    </Grid.Row>

                    <Grid.Row>
                        <Grid.Column width={16}>
                            <Header
                                content='Contact Information'
                                color='teal'
                                sub
                            />
                        </Grid.Column>
                        <Grid.Column computer={8} tablet={8} mobile={16}>
                            <label>First Name</label>
                            <MyTextInput name='subscriberNameFirst' placeholder='First Name' tabIndex={5} />
                            
                            <label>Address</label>
                            <MyTextInput name='address' placeholder='Address' tabIndex={7} />
                        </Grid.Column>
                        <Grid.Column computer={8} tablet={8} mobile={16}>
                            <label>Last Name</label>
                            <MyTextInput name='subscriberNameLast' placeholder='Last Name' tabIndex={6} />

                            <label>Address 2</label>
                            <MyTextInput name='address2' placeholder='Address 2' tabIndex={8} />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column computer={5} tablet={5} mobile={16}>
                            <label>City</label>
                            <MyTextInput name='city' placeholder='City' tabIndex={9} />
                        </Grid.Column>
                        <Grid.Column computer={5} tablet={5} mobile={16}>
                            <label>State</label>
                            <MySelectInput 
                                options={stateOptions} 
                                placeholder='--- Select State ---'  
                                name='state' 
                                tabIndex={10} 
                            />
                        </Grid.Column>
                        <Grid.Column computer={5} tablet={5} mobile={16}>
                            <label>Zip Code</label>
                            <MyZipCodeInput name='zipCode' placeholder='Zip Code' tabIndex={11} />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column width={16}>
                            <ErrorMessage 
                                name='error' render={() => 
                                <ValidationErrors errors={errors.error}/>}
                            />
                            <Button 
                                disabled={!dirty || isSubmitting}
                                loading={isSubmitting}
                                color='teal' 
                                content={values.subscriberID > 0 ? 'Update' : 'Register'}
                                type='submit'
                                fluid 
                            />
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Form>
            )}
        </Formik>
    )
})